// libraries
import { gql, GraphQLClient } from 'graphql-request';
import { BASE_URL, headers, headers2, PROD_SSR_GQL_ENDPOINT } from '../configs/constants';
import {
  handleLeadBloqueado,
  isLeadBloqueadoResponse,
  LeadBloqueadoError,
} from '../lib/lead-blocked';
import { buildIdCidadesGraphqlArg, getRegiaoSlugFromBrowser } from '../lib/region';
import { argsGraphqlBuscaIa, ExtrasBuscaIa } from '../utils/buscaIa';
import { argsQtdLista, qtdEscalar } from '../utils/qtdMultiSelecao';
import { isPositiveGraphqlInt } from '../utils/graphql-int';
import { normalizeHost } from '../utils/url';
import { guardaUtmDaPrimeiraVisita } from '../utils/utmNoWhats';

const BOT_PATTERNS = [
  /googlebot/i, /bingbot/i, /baiduspider/i, /yandexbot/i,
  /duckduckbot/i, /facebookexternalhit/i, /ia_archiver/i,
  /crawler/i, /spider/i, /bot/i
];

const getGraphqlRequestOrigin = (origin?: string) =>
  typeof window !== 'undefined' ? window.location.origin : origin || '';

const getSsrForwardedHostHeaders = (origin?: string) => {
  if (typeof window !== 'undefined') return {};

  const normalizedOriginHost = normalizeHost(origin || '');
  if (!normalizedOriginHost) return {};

  return {
    'x-forwarded-host': normalizedOriginHost,
  };
};

function isLikelyBot() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (BOT_PATTERNS.some((re) => re.test(ua))) return true;
  if ((navigator as any).webdriver) return true; // automação
  return false;
}

// MULTI-REGIÃO: monta o trecho `,id_cidades: [..]` (arg do contrato público —
// lista IN de ids de cidade). Só é aplicado quando NÃO há id_cidade escalar:
// cidade escolhida explicitamente pelo usuário (ou fixada no carrossel) VENCE
// a região. A sanitização mora em lib/region (fonte única).
function buildIdCidadesArg(idCity: any, idCidades?: number[] | null): string {
  if (idCity) return '';
  return buildIdCidadesGraphqlArg(idCidades);
}

// BUSCA COM SELEÇÃO MÚLTIPLA (utils/buscaMultiSelecao): condomínio e categoria chegam como
// LISTA quando o visitante marca 2+ — aí vão nos args de lista do contrato (`id_empreendimentos`,
// `id_categorias`). Escalar (todos os outros casos) sai exatamente como antes.
function argEmpreendimentoBusca(company: any): string {
  if (Array.isArray(company)) {
    const ids = company.map(Number).filter((v) => Number.isInteger(v) && v > 0);
    return ids.length > 0 ? `,id_empreendimentos: [${ids.join(',')}]` : '';
  }
  return company ? `,id_empreendimento: ${company}` : '';
}

function argCategoriaBusca(id_categoria: any): string {
  if (Array.isArray(id_categoria)) {
    const ids = id_categoria.map(Number).filter((v) => Number.isInteger(v) && v > 0);
    return ids.length > 0 ? `,id_categorias: [${ids.join(',')}]` : '';
  }
  return id_categoria > 0 ? ",id_categoria:" + id_categoria : '';
}

// requests
//query para pegar os imoveis para o carrossel de acordo com os filtros passados
export async function properties_carrossel(info: any, url_b?: any, origin?: string, codsite?: any, idCidades?: number[] | null, opts?: { comExclu?: boolean }) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    let endpoint = BASE_URL;

    if(typeof window === 'undefined' && url_b)
        endpoint = `${(url_b || '').replace(/\/$/, '')}/api/gql`
    else
        endpoint = '/api/gql'

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: getGraphqlRequestOrigin(origin),
            'x-graphql-client-name': getGraphqlRequestOrigin(origin),
            codsite: codsite ? codsite : 0,
            ...getSsrForwardedHostHeaders(origin)
        },
    });

    const query = gql`
        {
            imoveis(pagina: 1, ordenar: Inclusao, ordem: desc 
            ${info.id_tipo ? ', id_tipo: ' + info.id_tipo : ''}
            ${info.venda || ((info.preco_max > 0 || info.preco_min > 0) && !info.venda && !info.aluguel) ? ', venda: ' + 1 : ''}
            ${info.aluguel ? ', aluguel: ' + 1 : ''}
            ${info.id_categoria > 0 ? `,id_categoria: ${info.id_categoria}` : ''}
            ${info.id_cidade ? `,id_cidade: ${info.id_cidade}` : buildIdCidadesArg(info.id_cidade, idCidades)}
            ${info.id_bairro ? `,id_bairro: ${[info.id_bairro]}` : ''}
            ${info.preco_min && (info.venda || (!info.venda && !info.aluguel)) ? `,preco_venda_min: ${info.preco_min}` : ''}
            ${info.preco_max > info.preco_min && (info.venda || (!info.venda && !info.aluguel)) ? `,preco_venda_max: ${info.preco_max}` : ''}
            ${info.preco_min && info.aluguel ? `,preco_locacao_min: ${info.preco_min}` : ''}
            ${info.preco_max > info.preco_min && info.aluguel ? `,preco_locacao_max: ${info.preco_max}` : ''}
            ${info.id_empreendimento ? `,id_empreendimento: ${info.id_empreendimento}`: ''}
            ${info.id_caracteristica > 0 ? ",id_caracteristic: "+ info.id_caracteristica : ''}
        ) {
                count,
                imoveis {
                    id
                    ${opts?.comExclu ? 'exclu' : ''}
                    mini_banner_texto
                    qtd_salas
                    pais
                    area_tipo
                    tipo {
                        id
                        nome
                    }
                    categoria {
                        id
                        nome
                    }
                    cidade {
                        id
                        nome
                    }
                    bairro {
                        id
                        nome
                        id_cidade
                    }
                    referencia
                    data_entrega
                    temporada
                    data_edicao
                    id
                    d1
                    disponibilidade
                    venda
                    aluguel
                    endereco
                    titulo
                    descricao
                    dormitorios
                    suites
                    bloquear_fotos
                    banheiros
                    garagems
                    boxes
                    origem
                    mostrar_endereco
                    preco_venda
                    exibir_preco_venda
                    moeda_imovel
                    preco_iptu
                    destaque
                    link_video
                    tour360
                    preco_locacao
                    esconder_home
                    exibir_preco_locacao
                    exibir_preco_iptu
                    exibir_preco_condominio
                    preco_condominio
                    area_construida
                    area_total
                    terreno
                    area_privativa
                    area_util
                    latitude_longitude
                    palavras_chave
                    fotos {
                        id
                        id_imovel
                        nome
                        destaque
                        posicao
                        url_foto
                        url_marca
                        url_spaces_cdn
                        url_spaces_migracao
                    }
                    caracteristicas {
                        caracteristica {
                            id
                            nome
                            tipo
                        }
                    }
                    visualizacoes
                    preco_especial {
                        preco_especial_venda
                        preco_especial_locacao
                    }
                    empreendimento {
                        id
                        nome
                        referencia
                        endereco
                        destaque
                        meta_description
                        meta_keywords
                        meta_title
                        descricao
                        foto_destaque
                    }
                }
            }
        }
    `;

    try {
        const data = await graphQLClient.request(query);
        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os imoveis com filtros
export async function properties(page: number, id?: number, id_tipo?: any, temporada?: number, url_b?: any, origin?: string, codsite?: number, venda?: number, aluguel?: number) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    const endpoint = url_b || BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });

    const query = gql`
        {
            imoveis(pagina: ${page}, ordenar: Inclusao, ordem: desc ${
        id ? `, id: ${id}` : ''}
        ${id_tipo ? ', id_tipo: ' + id_tipo : ''}
        ${temporada ? ', temporada: ' + temporada : ''}
        ${venda ? ', venda: ' + venda : ''}
        ${aluguel ? ', aluguel: ' + aluguel : ''}
        ) {
                count,
                imoveis {
                    id
                    mini_banner_texto
                    qtd_salas
                    pais
                    area_tipo
                    tipo {
                        id
                        nome
                    }
                    categoria {
                        id
                        nome
                    }
                    cidade {
                        id
                        nome
                    }
                    bairro {
                        id
                        nome
                        id_cidade
                    }
                    referencia
                    data_entrega
                    temporada
                    data_edicao
                    id
                    d1
                    disponibilidade
                    venda
                    aluguel
                    endereco
                    titulo
                    descricao
                    dormitorios
                    suites
                    bloquear_fotos
                    banheiros
                    garagems
                    boxes
                    origem
                    mostrar_endereco
                    preco_venda
                    exibir_preco_venda
                    moeda_imovel
                    preco_iptu
                    destaque
                    link_video
                    tour360
                    preco_locacao
                    esconder_home
                    exibir_preco_locacao
                    exibir_preco_iptu
                    exibir_preco_condominio
                    preco_condominio
                    area_construida
                    area_total
                    terreno
                    area_privativa
                    area_util
                    latitude_longitude
                    palavras_chave
                    fotos {
                        id
                        id_imovel
                        nome
                        destaque
                        posicao
                        url_foto
                        url_marca
                        url_spaces_cdn
                        url_spaces_migracao
                    }
                    caracteristicas {
                        caracteristica {
                            id
                            nome
                            tipo
                        }
                    }
                    visualizacoes
                    preco_especial {
                        preco_especial_venda
                        preco_especial_locacao
                    }
                    empreendimento {
                        id
                        nome
                        referencia
                        endereco
                        destaque
                        meta_description
                        meta_keywords
                        meta_title
                        descricao
                        foto_destaque
                    }
                }
            }
        }
    `;

    try {
        const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os imoveis de um empreendimento
export async function imoveis_empreendimento(id_empreendimento: number, origin?: string, codsite?: number) {
    if ((!origin && typeof window === 'undefined') || !id_empreendimento) {
        return null;
    }

    const endpoint = BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });

    const query = gql`
    {
        imoveis(pagina: 1, ordenar: Inclusao, ordem: desc, id_empreendimento: ${id_empreendimento})
        {
            count
            imoveis {
                id
                estado
                qtd_salas
                pais
                tipo {
                    nome
                }
                cidade {
                    nome
                }
                bairro {
                    nome
                }
                exclu
                referencia
                data_entrega
                transacao
                id_empreendimento
                venda
                aluguel
                temporada
                finalidade
                cep
                endereco
                numero
                complemento
                titulo
                dormitorios
                suites
                origem
                banheiros
                garagems
                boxes
                situacao
                mini_banner_texto
                data_edicao
                mostrar_endereco
                tour360
                preco_venda
                exibir_preco_venda
                moeda_imovel
                preco_condominio
                disponibilidade
                d1
                disponivel_t
                disponivel
                destaque
                esconder_home
                link_video
                preco_locacao
                exibir_preco_locacao
                area_construida
                area_total
                terreno
                area_privativa
                area_util
                latitude_longitude
                fotos {
                    url_foto
                    url_foto_menor
                    url_marca
                    destaque
                    url_spaces_cdn
                    url_spaces_migracao
                }
                preco_especial {
                    preco_especial_locacao
                    preco_especial_venda
                }
                empreendimento {
                    nome
                    foto_destaque
                }
                area_tipo
            }
        }
    }`;

    
    try {
        
const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}


//query para pegar os 12 imoveis mais novos para colocar na home
// `quantidade` (15/10): o mosaico do Atelier pede 40 fotos; os demais chamadores seguem nos 12 de sempre.
export async function imoveis_novidades(url_b?: any, origin?: string, codsite?: number, url?: any, idCidades?: number[] | null, opts?: { comCategoria?: boolean; quantidade?: number }) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    let endpoint = BASE_URL;

    if(typeof window === 'undefined' && url_b)
        endpoint = `${(url_b || '').replace(/\/$/, '')}/api/gql`
    else
        endpoint = '/api/gql'

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: getGraphqlRequestOrigin(origin),
            'x-graphql-client-name': getGraphqlRequestOrigin(origin),
            codsite: codsite ? codsite : 0,
            query: 'imoveis_novidades',
            ...getSsrForwardedHostHeaders(origin)
        },
    });

    const query = gql`
    {
        imoveis_novidades(quantidade: ${Number(opts?.quantidade) > 0 ? Math.floor(Number(opts?.quantidade)) : 12}${buildIdCidadesArg(null, idCidades)}) {
            exclu
            ${opts?.comCategoria ? 'categoria { id nome }' : ''}
            id
            estado
            pais
            tipo {
                nome
            }
            cidade {
                nome
            }
            bairro {
                nome
            }
            referencia
            data_entrega
            transacao
            id_empreendimento
            venda
            aluguel
            temporada
            finalidade
            cep
            endereco
            numero
            complemento
            titulo
            origem
            dormitorios
            suites
            banheiros
            garagems
            boxes
            situacao
            mini_banner_texto
            data_edicao
            mostrar_endereco
            tour360
            preco_venda
            exibir_preco_venda
            moeda_imovel
            preco_condominio
            disponibilidade
            d1
            disponivel_t
            disponivel
            destaque
            esconder_home
            link_video
            preco_locacao
            exibir_preco_locacao
            area_construida
            area_total
            terreno
            area_privativa
            area_util
            fotos {
                url_foto
                url_foto_menor
                url_marca
                url_spaces_cdn
                url_spaces_migracao
                destaque
            }
            preco_especial {
                preco_especial_locacao
                preco_especial_venda
            }
            empreendimento {
                nome
                foto_destaque
            }
            area_tipo
        }
    }
    `;

    try {
        const data = await graphQLClient.request(query);
  
        return data;
    } catch (error) {
        return null;
    }
}

/**
 * Top 10 marcado com UMA transação só (venda XOR aluguel) → 'venda' | 'aluguel'; senão null.
 *
 * cs2639 (14/09/2026): "Destaques de Vendas" e "Destaques de Locação" eram Top 10, e o
 * `imoveis_destaque` não recebe transação — os dois carrosséis mostravam a MESMA lista, com venda
 * e locação misturadas (a ref 705, de locação, era o 1º card do carrossel de venda). Havia 45
 * carrosséis Top 10 marcados com uma transação só. Marcado com as duas (ou com nenhuma), o Top 10
 * continua exatamente como era.
 */
export function transacaoUnicaTop10(carrossel: any): 'venda' | 'aluguel' | null {
    const venda = carrossel?.venda === true || Number(carrossel?.venda) === 1;
    const aluguel = carrossel?.aluguel === true || Number(carrossel?.aluguel) === 1;
    if (venda === aluguel) return null;
    return venda ? 'venda' : 'aluguel';
}

//query para pegar os 10 imoveis em destaque para colocar no carrossel da home
export async function propertiesTop10(url_b?: any, origin?: string, codsite?: number, url?: any, idCidades?: number[] | null, carrossel?: any, opts?: { comCategoria?: boolean }) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    let endpoint = BASE_URL;

    if(typeof window === 'undefined' && url_b)
        endpoint = `${(url_b || '').replace(/\/$/, '')}/api/gql`
    else
        endpoint = '/api/gql'

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: getGraphqlRequestOrigin(origin),
            'x-graphql-client-name': getGraphqlRequestOrigin(origin),
            codsite: codsite ? codsite : 0,
            query: 'imoveis_destaque',
            ...getSsrForwardedHostHeaders(origin)
        },
    });

    // Transação única → destaques SÓ daquela transação, pela busca: `imoveis` aceita destaque +
    // venda/aluguel + id_cidades, e `imoveis_destaque` só quantidade/id_cidades. Mesmos campos e
    // mesmo formato de retorno (`imoveis_destaque`), então quem chama não muda.
    const transacao = transacaoUnicaTop10(carrossel);
    const campos = `
        exclu
        ${opts?.comCategoria ? 'categoria { id nome }' : ''}
        id
        estado
        pais
        tipo {
            nome
        }
        cidade {
            nome
        }
        bairro {
            nome
        }
        referencia
        data_entrega
        transacao
        id_empreendimento
        venda
        aluguel
        temporada
        finalidade
        cep
        endereco
        numero
        complemento
        titulo
        dormitorios
        suites
        banheiros
        origem
        garagems
        boxes
        situacao
        mini_banner_texto
        data_edicao
        mostrar_endereco
        tour360
        preco_venda
        exibir_preco_venda
        moeda_imovel
        preco_condominio
        disponibilidade
        d1
        disponivel_t
        disponivel
        destaque
        esconder_home
        link_video
        preco_locacao
        exibir_preco_locacao
        area_construida
        area_total
        terreno
        area_privativa
        area_util
        fotos {
            url_foto
            url_foto_menor
            url_marca
            destaque
            url_spaces_cdn
            url_spaces_migracao
        }
        preco_especial {
            preco_especial_locacao
            preco_especial_venda
        }
        empreendimento {
            nome
            foto_destaque
        }
        area_tipo
    `;

    const query = transacao
        ? gql`{
        imoveis(pagina: 1, qtd_pag: 10, ordenar: Inclusao, ordem: desc, destaque: true, ${transacao}: 1${buildIdCidadesArg(null, idCidades)}) {
            imoveis {${campos}}
        }
    }
    `
        : gql`{
        imoveis_destaque(quantidade: 10${buildIdCidadesArg(null, idCidades)}) {${campos}}
    }
    `;

    try {
        const data: any = await graphQLClient.request(query);
        if (transacao) return { imoveis_destaque: data?.imoveis?.imoveis ?? [] };
        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os imoveis para o carrossel da home com filtros personalizado
export async function carrossel_select(carrossel: number, url_b?: any, origin?: string, codsite?: number, url?: any, idCidades?: number[] | null) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    let endpoint = BASE_URL;

    if(url == 'teste' || url_b){
        endpoint = '/api/gql';
    }

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `carrossel_select_${carrossel}`
        },
    });

    const query = gql`{
        carrossel_select(quantidade: 12,carrossel: ${carrossel}${buildIdCidadesArg(null, idCidades)}) {
        exclu
        id
        estado
        pais
        tipo {
            nome
        }
        cidade {
            nome
        }
        bairro {
            nome
        }
        referencia
        data_entrega
        transacao
        id_empreendimento
        venda
        aluguel
        temporada
        finalidade
        cep
        endereco
        numero
        complemento
        titulo
        origem
        dormitorios
        suites
        banheiros
        garagems
        boxes
        situacao
        mini_banner_texto
        data_edicao
        mostrar_endereco
        tour360
        preco_venda
        exibir_preco_venda
        moeda_imovel
        preco_condominio
        disponibilidade
        d1
        disponivel_t
        disponivel
        destaque
        esconder_home
        link_video
        preco_locacao
        exibir_preco_locacao
        area_construida
        area_total
        terreno
        area_privativa
        area_util
        fotos {
            url_foto
            url_foto_menor
            url_marca
            destaque
            url_spaces_cdn
            url_spaces_migracao
        }
        preco_especial {
            preco_especial_locacao
            preco_especial_venda
        }
        empreendimento {
            nome
            foto_destaque
        }
        area_tipo
        }
    }
    `;

    try {
            const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os imoveis para o carrossel da home com filtros personalizado
export async function propertiesFilter(
    id_categoria: any,
    page: number,
    idCity?: number | null,
    idBairro?: any | null,
    idTipo?: any | null,
    referencia?: any,
    precoVendaMin?: number | null,
    precoVendaMax?: number | null,
    precoLocacaoMin?: number | null,
    precoLocacaoMax?: number | null,
    venda?: number,
    aluguel?: number,
    company?: number | number[],
    ordenar?: any,
    garagemsMin?: number | number[] | null,
    dormitoriosMin?: number | number[] | null,
    suitesMin?: number | number[] | null,
    banheirosMin?: number | number[] | null,
    agenciador?: Number | null,
    precoTemporadaMin?: number | null,
    precoTemporadaMax?: number | null,
    temporada?: number,
    id_imovel?: any | null,
    origin?: string,
    codsite?: number,
    id_caracteristica?: any,
    area_min?: number,
    area_max?: number,
    mapa?: number,
    ano_entr?: any,
    id_estado?: any,
    signal?: AbortSignal,
    // MULTI-REGIÃO: cidades da região ativa (só aplicado sem idCity)
    idCidades?: number[] | null,
    // BUSCA POR IA (INC-0018 F4): mínimos sem teto + ponto do "perto" (utils/buscaIa).
    // Ausente = query idêntica à de sempre.
    extrasBuscaIa?: ExtrasBuscaIa | null,
    )
    {
        if (!origin && typeof window === 'undefined') {
            return null;
        }

        // Atelier (utils/qtdMultiSelecao): 2+ números marcados chegam em LISTA → args *_lista, sem min/max.
        const argsListaQtd = argsQtdLista({ garagems: garagemsMin, dormitorios: dormitoriosMin, suites: suitesMin, banheiros: banheirosMin })
        garagemsMin = qtdEscalar(garagemsMin)
        dormitoriosMin = qtdEscalar(dormitoriosMin)
        suitesMin = qtdEscalar(suitesMin)
        banheirosMin = qtdEscalar(banheirosMin)
        let suitesMax = suitesMin
        if(suitesMin == 5)
            suitesMax = 0
        let garagemsMax = garagemsMin
        if(garagemsMin == 5)
            garagemsMax = 0
        let dormitoriosMax = dormitoriosMin
        if(dormitoriosMin == 5)
            dormitoriosMax = 0
        let banheirosMax = banheirosMin
        if(banheirosMin == 5)
            banheirosMax = 0
        const argsBuscaIa = argsGraphqlBuscaIa(extrasBuscaIa, {
            dormitorios_min: dormitoriosMin,
            suites_min: suitesMin,
            banheiros_min: banheirosMin,
            garagems_min: garagemsMin,
        })

        const endpoint = BASE_URL;

        const graphQLClient = new GraphQLClient(endpoint, {
            headers: {
                ...headers,
                dominion: typeof window !== 'undefined' ? window.location.origin : origin,
                'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
                codsite: codsite ? codsite : 0
            },
        });

    const query = gql`
    {
        imoveis_busca(pagina: ${page},
        ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
        ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
        ${argCategoriaBusca(id_categoria)}
        ${id_imovel && id_imovel != undefined && id_imovel != "" ? ",id: ["+id_imovel+"]" : ''}
        ${idCity ? `,id_cidade: ${idCity}` : ''}
        ${buildIdCidadesArg(idCity, idCidades)}
        ${idBairro ? `,id_bairro: ${idBairro}` : ''}
        ${idTipo ? `,id_tipo: ${idTipo}` : ''}
        ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
        ${aluguel ? `,aluguel: ${aluguel}` : ''}
        ${temporada ? `,temporada: ${temporada}` : ''}
        ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
        ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax}` : ''}
        ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
        ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
        ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
        ${argEmpreendimentoBusca(company)}
        ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
        ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
        ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
        ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
        ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
        ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
        ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
        ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
        ${area_min ? `,metragem_min: ${area_min}` : ''}
        ${area_max ? `,metragem_max: ${area_max}` : ''}
        ${id_estado ? `,id_estado: ${id_estado}` : ''}
        ${id_caracteristica && id_caracteristica != undefined && id_caracteristica != "" ? ",id_caracteristic: "+id_caracteristica : ''}
        ${ano_entr && ano_entr != undefined && ano_entr != "" ? ",ano_entrega: "+ano_entr : ''}
        ${mapa ? `,mapa: ${mapa}` : ''}${argsBuscaIa}
    ) {
        count
            imoveis {
                id
                estado
                pais
                tipo {
                    nome
                    id
                }
                categoria {
                    nome
                    id
                }
                cidade {
                    id
                    nome
                }
                bairro {
                    nome
                    id
                }
                referencia
                data_entrega
                transacao
                exclu
                id_empreendimento
                venda
                aluguel
                temporada
                finalidade
                titulo
                descricao
                origem
                dormitorios
                suites
                qtd_salas
                banheiros
                garagems
                boxes
                mini_banner_texto
                data_edicao
                mostrar_endereco
                incorporacao
                tour360
                preco_venda
                exibir_preco_venda
                moeda_imovel
                d1
                esconder_home
                link_video
                preco_locacao
                exibir_preco_locacao
                area_construida
                area_total
                terreno
                area_privativa
                area_util
                fotos {
                    url_foto
                }
                    preco_especial {
                    preco_especial_locacao
                    preco_especial_venda
                }
                empreendimento {
                    id
                    nome
                    foto_destaque
                }
                area_tipo
            }
        }
    }
    `;

    try {

        const data = signal
            ? await graphQLClient.request({ document: query, signal } as any)
            : await graphQLClient.request(query);

        return data;
    } catch (error: any) {
        // Resiliência: o GraphQLClient lança quando há QUALQUER erro de campo, mesmo
        // com dados parciais válidos (ex.: backend tipou `boxes` como Int e um imóvel
        // tem "33/34" → erro só naquele campo, mas os imóveis vêm na resposta). Sem isto
        // a lista inteira virava null → 0 imóveis + loop. Aproveita os dados parciais.
        const partial = error?.response?.data;
        if (partial && Object.keys(partial).length > 0) return partial;
        return null;
    }
}

// LISTA + MAPA numa única requisição (só tenants com mostrar_mapa). NÃO renomear
// o campo `imoveis_busca` (BFF mantém a busca não-cacheada).
export async function propertiesFilterWithMap(
    id_categoria: any,
    page: number,
    idCity?: number | null,
    idBairro?: any | null,
    idTipo?: any | null,
    referencia?: any,
    precoVendaMin?: number | null,
    precoVendaMax?: number | null,
    precoLocacaoMin?: number | null,
    precoLocacaoMax?: number | null,
    venda?: number,
    aluguel?: number,
    company?: number | number[],
    ordenar?: any,
    garagemsMin?: number | number[] | null,
    dormitoriosMin?: number | number[] | null,
    suitesMin?: number | number[] | null,
    banheirosMin?: number | number[] | null,
    agenciador?: Number | null,
    precoTemporadaMin?: number | null,
    precoTemporadaMax?: number | null,
    temporada?: number,
    id_imovel?: any | null,
    origin?: string,
    codsite?: number,
    id_caracteristica?: any,
    area_min?: number,
    area_max?: number,
    mapa?: number,
    ano_entr?: any,
    id_estado?: any,
    signal?: AbortSignal,
    // MULTI-REGIÃO: cidades da região ativa (só aplicado sem idCity)
    idCidades?: number[] | null,
    // BUSCA POR IA (INC-0018 F4): mesmos extras da lista, nos DOIS campos (lista e mapa).
    extrasBuscaIa?: ExtrasBuscaIa | null,
    )
    {
        if (!origin && typeof window === 'undefined') {
            return null;
        }

        // Atelier (utils/qtdMultiSelecao): 2+ números marcados chegam em LISTA → args *_lista, sem min/max.
        const argsListaQtd = argsQtdLista({ garagems: garagemsMin, dormitorios: dormitoriosMin, suites: suitesMin, banheiros: banheirosMin })
        garagemsMin = qtdEscalar(garagemsMin)
        dormitoriosMin = qtdEscalar(dormitoriosMin)
        suitesMin = qtdEscalar(suitesMin)
        banheirosMin = qtdEscalar(banheirosMin)
        let suitesMax = suitesMin
        if(suitesMin == 5) suitesMax = 0
        let garagemsMax = garagemsMin
        if(garagemsMin == 5) garagemsMax = 0
        let dormitoriosMax = dormitoriosMin
        if(dormitoriosMin == 5) dormitoriosMax = 0
        let banheirosMax = banheirosMin
        if(banheirosMin == 5) banheirosMax = 0
        const argsBuscaIa = argsGraphqlBuscaIa(extrasBuscaIa, {
            dormitorios_min: dormitoriosMin,
            suites_min: suitesMin,
            banheiros_min: banheirosMin,
            garagems_min: garagemsMin,
        })

        const endpoint = BASE_URL;
        const graphQLClient = new GraphQLClient(endpoint, {
            headers: {
                ...headers,
                dominion: typeof window !== 'undefined' ? window.location.origin : origin,
                'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
                codsite: codsite ? codsite : 0
            },
        });

        const query = gql`
        {
                    imoveis_busca(pagina: ${page},
                    ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
                    ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
                    ${argCategoriaBusca(id_categoria)}
                    ${id_imovel && id_imovel != undefined && id_imovel != "" ? ",id: ["+id_imovel+"]" : ''}
                    ${idCity ? `,id_cidade: ${idCity}` : ''}
                    ${buildIdCidadesArg(idCity, idCidades)}
                    ${idBairro ? `,id_bairro: ${idBairro}` : ''}
                    ${idTipo ? `,id_tipo: ${idTipo}` : ''}
                    ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
                    ${aluguel ? `,aluguel: ${aluguel}` : ''}
                    ${temporada ? `,temporada: ${temporada}` : ''}
                    ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
                    ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax}` : ''}
                    ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
                    ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
                    ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
                    ${argEmpreendimentoBusca(company)}
                    ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
                    ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
                    ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
                    ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
                    ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
                    ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
                    ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
                    ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
                    ${area_min ? `,metragem_min: ${area_min}` : ''}
                    ${area_max ? `,metragem_max: ${area_max}` : ''}
                    ${id_estado ? `,id_estado: ${id_estado}` : ''}
                    ${id_caracteristica && id_caracteristica != undefined && id_caracteristica != "" ? ",id_caracteristic: "+id_caracteristica : ''}
                    ${ano_entr && ano_entr != undefined && ano_entr != "" ? ",ano_entrega: "+ano_entr : ''}
                    ${mapa ? `,mapa: ${mapa}` : ''}${argsBuscaIa}
                ) {
                    count
                        imoveis {
                            id
                            estado
                            pais
                            tipo {
                                nome
                                id
                            }
                            categoria {
                                nome
                                id
                            }
                            cidade {
                                id
                                nome
                            }
                            bairro {
                                nome
                                id
                            }
                            referencia
                            data_entrega
                            transacao
                            exclu
                            id_empreendimento
                            venda
                            aluguel
                            temporada
                            finalidade
                            titulo
                            descricao
                            origem
                            dormitorios
                            suites
                            qtd_salas
                            banheiros
                            garagems
                            boxes
                            mini_banner_texto
                            data_edicao
                            mostrar_endereco
                            incorporacao
                            tour360
                            preco_venda
                            exibir_preco_venda
                            moeda_imovel
                            d1
                            esconder_home
                            link_video
                            preco_locacao
                            exibir_preco_locacao
                            area_construida
                            area_total
                            terreno
                            area_privativa
                            area_util
                            fotos {
                                url_foto
                            }
                                preco_especial {
                                preco_especial_locacao
                                preco_especial_venda
                            }
                            empreendimento {
                                id
                                nome
                                foto_destaque
                            }
                            area_tipo
                        }
                    }
    
                        mapa: imoveis(pagina: 1,
                        ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
                        ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
                ${argCategoriaBusca(id_categoria)}
                ${idCity ? `,id_cidade: ${idCity}` : ''}
                ${buildIdCidadesArg(idCity, idCidades)}
                ${idBairro ? `,id_bairro: ${idBairro}` : ''}
                ${idTipo ? `,id_tipo: ${idTipo}` : ''}
                ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
                ${aluguel ? `,aluguel: ${aluguel}` : ''}
                ${temporada ? `,temporada: ${temporada}` : ''}
                ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
                ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax}` : ''}
                ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
                ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
                ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
                ${argEmpreendimentoBusca(company)}
                ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
                ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
                ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
                ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
                ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
                ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
                ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
                ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
                ,mapa: 100${argsBuscaIa}
                ) {
                    count,
                    imoveis {
                        exclu
                        id
                        mini_banner_texto
                        tipo {
                            nome
                        }
                        referencia
                        data_entrega
                        venda
                        aluguel
                        qtd_salas
                        temporada
                        area_total
                        area_tipo
                        area_privativa
                        bairro {
                            nome
                        }
                        cidade {
                            nome
                        }
                        temporada
                        latitude_longitude
                        titulo
                        fotos {
                            url_foto
                            url_spaces_cdn
                            url_spaces_migracao
                        }
                    }
                    }
    
        }
        `;

        try {
            const data = signal
                ? await graphQLClient.request({ document: query, signal } as any)
                : await graphQLClient.request(query);
            return data;
        } catch (error: any) {
            // falha parcial: aproveita o que veio (lista OU mapa) — um campo com erro
            // não derruba o outro. Mesmo padrão de propertiesFilter.
            const partial = error?.response?.data;
            if (partial && Object.keys(partial).length > 0) return partial;
            return null;
        }
    }


// LISTA + MAPA numa única requisição (só tenants com mostrar_mapa). NÃO renomear
// o campo `imoveis_promocao` (BFF mantém a busca não-cacheada).
export async function propertiesFilterPromocaoWithMap(
    id_categoria: any,
    page: number,
    idCity?: number | null,
    idBairro?: any | null,
    idTipo?: any | null,
    referencia?: any,
    precoVendaMin?: number | null,
    precoVendaMax?: number | null,
    precoLocacaoMin?: number | null,
    precoLocacaoMax?: number | null,
    venda?: number,
    aluguel?: number,
    company?: number | number[],
    ordenar?: any,
    garagemsMin?: number | number[] | null,
    dormitoriosMin?: number | number[] | null,
    suitesMin?: number | number[] | null,
    banheirosMin?: number | number[] | null,
    agenciador?: Number | null,
    precoTemporadaMin?: number | null,
    precoTemporadaMax?: number | null,
    temporada?: number,
    id_imovel?: any | null,
    origin?: string, codsite?: number, signal?: AbortSignal,
    // MULTI-REGIÃO: cidades da região ativa (só aplicado sem idCity)
    idCidades?: number[] | null
    )
    {
        if (!origin && typeof window === 'undefined') {
            return null;
        }

        // Atelier (utils/qtdMultiSelecao): 2+ números marcados chegam em LISTA → args *_lista, sem min/max.
        const argsListaQtd = argsQtdLista({ garagems: garagemsMin, dormitorios: dormitoriosMin, suites: suitesMin, banheiros: banheirosMin })
        garagemsMin = qtdEscalar(garagemsMin)
        dormitoriosMin = qtdEscalar(dormitoriosMin)
        suitesMin = qtdEscalar(suitesMin)
        banheirosMin = qtdEscalar(banheirosMin)
        let suitesMax = suitesMin
        if(suitesMin == 5) suitesMax = 0
        let garagemsMax = garagemsMin
        if(garagemsMin == 5) garagemsMax = 0
        let dormitoriosMax = dormitoriosMin
        if(dormitoriosMin == 5) dormitoriosMax = 0
        let banheirosMax = banheirosMin
        if(banheirosMin == 5) banheirosMax = 0

        const endpoint = BASE_URL;
        const graphQLClient = new GraphQLClient(endpoint, {
            headers: {
                ...headers,
                dominion: typeof window !== 'undefined' ? window.location.origin : origin,
                'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
                codsite: codsite ? codsite : 0
            },
        });

        const query = gql`
        {
                        imoveis_promocao(pagina: ${page},
                        ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
                        ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
                ${argCategoriaBusca(id_categoria)}
                ${id_imovel && id_imovel != undefined && id_imovel != "" ? ",id: ["+id_imovel+"]" : ''}
                ${idCity ? `,id_cidade: ${idCity}` : ''}
                ${buildIdCidadesArg(idCity, idCidades)}
                ${idBairro ? `,id_bairro: ${idBairro}` : ''}
                ${idTipo ? `,id_tipo: ${idTipo}` : ''}
                ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
                ${aluguel ? `,aluguel: ${aluguel}` : ''}
                ${temporada ? `,temporada: ${temporada}` : ''}
                ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
                ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax}` : ''}
                ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
                ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
                ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
                ${argEmpreendimentoBusca(company)}
                ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
                ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
                ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
                ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
                ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
                ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
                ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
                ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
                ) {
                    count,
                    imoveis {
                        exclu
                        id
                        mini_banner_texto
                        area_tipo
                        pais
                        qtd_salas
                        tipo {
                            id
                            nome
                        }
                        categoria {
                            id
                            nome
                        }
                        cidade {
                            id
                            nome
                        }
                        bairro {
                            id
                            nome
                            id_cidade
                        }
                        referencia
                        data_entrega
                        venda
                        aluguel
                        temporada
                        data_edicao
                        id
                        d1
                        disponibilidade
                        endereco
                        titulo
                        descricao
                        dormitorios
                        suites
                        banheiros
                        garagems
                        boxes
                        origem
                        bloquear_fotos
                        mostrar_endereco
                        preco_venda
                        exibir_preco_venda
                        moeda_imovel
                        preco_iptu
                        destaque
                        link_video
                        tour360
                        preco_locacao
                        esconder_home
                        exibir_preco_locacao
                        exibir_preco_iptu
                        area_construida
                        area_total
                        terreno
                        area_privativa
                        area_util
                        palavras_chave
                        fotos {
                            id
                            id_imovel
                            nome
                            destaque
                            url_spaces_cdn
                            url_spaces_migracao
                            posicao
                            url_foto
                            url_marca
                        }
                        caracteristicas {
                            caracteristica {
                                id
                                nome
                                tipo
                            }
                        }
                        visualizacoes
                        preco_especial {
                            preco_especial_venda
                            preco_especial_locacao
                        }
                        empreendimento {
                            id
                            nome
                            referencia
                            endereco
                            destaque
                            meta_description
                            meta_keywords
                            meta_title
                            descricao
                            foto_destaque
                        }
                    }
                    }
    
                        mapa: imoveis_promocao(pagina: 1,
                        ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
                        ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
                ${argCategoriaBusca(id_categoria)}
                ${idCity ? `,id_cidade: ${idCity}` : ''}
                ${buildIdCidadesArg(idCity, idCidades)}
                ${idBairro ? `,id_bairro: ${idBairro}` : ''}
                ${idTipo ? `,id_tipo: ${idTipo}` : ''}
                ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
                ${aluguel ? `,aluguel: ${aluguel}` : ''}
                ${temporada ? `,temporada: ${temporada}` : ''}
                ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
                ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax ? precoVendaMax : 100000000}` : ''}
                ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
                ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
                ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
                ${argEmpreendimentoBusca(company)}
                ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
                ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
                ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
                ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
                ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
                ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
                ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
                ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
                ,mapa: 100
                ) {
                    count,
                    imoveis {
                        exclu
                        id
                        mini_banner_texto
                        tipo {
                            nome
                        }
                        referencia
                        data_entrega
                        venda
                        qtd_salas
                        origem
                        aluguel
                        temporada
                        area_total
                        area_tipo
                        area_privativa
                        bairro {
                            nome
                        }
                        cidade {
                            nome
                        }
                        temporada
                        latitude_longitude
                        titulo
                        fotos {
                            url_foto
                            url_spaces_cdn
                            url_spaces_migracao
                        }
                    }
                    }
    
        }
        `;

        try {
            const data = signal
                ? await graphQLClient.request({ document: query, signal } as any)
                : await graphQLClient.request(query);
            return data;
        } catch (error: any) {
            // falha parcial: aproveita o que veio (lista OU mapa) — um campo com erro
            // não derruba o outro. Mesmo padrão de propertiesFilter.
            const partial = error?.response?.data;
            if (partial && Object.keys(partial).length > 0) return partial;
            return null;
        }
    }

export async function propertiesFilterPromocao(
    id_categoria: any,
    page: number,
    idCity?: number | null,
    idBairro?: any | null,
    idTipo?: any | null,
    referencia?: any,
    precoVendaMin?: number | null,
    precoVendaMax?: number | null,
    precoLocacaoMin?: number | null,
    precoLocacaoMax?: number | null,
    venda?: number,
    aluguel?: number,
    company?: number | number[],
    ordenar?: any,
    garagemsMin?: number | number[] | null,
    dormitoriosMin?: number | number[] | null,
    suitesMin?: number | number[] | null,
    banheirosMin?: number | number[] | null,
    agenciador?: Number | null,
    precoTemporadaMin?: number | null,
    precoTemporadaMax?: number | null,
    temporada?: number,
    id_imovel?: any | null,
    origin?: string, codsite?: number, signal?: AbortSignal,
    // MULTI-REGIÃO: cidades da região ativa (só aplicado sem idCity)
    idCidades?: number[] | null)
    {
        if (!origin && typeof window === 'undefined') {
            return null;
        }

        // Atelier (utils/qtdMultiSelecao): 2+ números marcados chegam em LISTA → args *_lista, sem min/max.
        const argsListaQtd = argsQtdLista({ garagems: garagemsMin, dormitorios: dormitoriosMin, suites: suitesMin, banheiros: banheirosMin })
        garagemsMin = qtdEscalar(garagemsMin)
        dormitoriosMin = qtdEscalar(dormitoriosMin)
        suitesMin = qtdEscalar(suitesMin)
        banheirosMin = qtdEscalar(banheirosMin)
        let suitesMax = suitesMin
        if(suitesMin == 5)
            suitesMax = 0
        let garagemsMax = garagemsMin
        if(garagemsMin == 5)
            garagemsMax = 0
        let dormitoriosMax = dormitoriosMin
        if(dormitoriosMin == 5)
            dormitoriosMax = 0
        let banheirosMax = banheirosMin
        if(banheirosMin == 5)
            banheirosMax = 0

        const endpoint = BASE_URL;

        const graphQLClient = new GraphQLClient(endpoint, {
            headers: {
                ...headers,
                dominion: typeof window !== 'undefined' ? window.location.origin : origin,
                'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
                codsite: codsite ? codsite : 0
            },
        });

    const query = gql`
        {
            imoveis_promocao(pagina: ${page},
            ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
            ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
    ${argCategoriaBusca(id_categoria)}
    ${id_imovel && id_imovel != undefined && id_imovel != "" ? ",id: ["+id_imovel+"]" : ''}
    ${idCity ? `,id_cidade: ${idCity}` : ''}
    ${buildIdCidadesArg(idCity, idCidades)}
    ${idBairro ? `,id_bairro: ${idBairro}` : ''}
    ${idTipo ? `,id_tipo: ${idTipo}` : ''}
    ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
    ${aluguel ? `,aluguel: ${aluguel}` : ''}
    ${temporada ? `,temporada: ${temporada}` : ''}
    ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
    ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax}` : ''}
    ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
    ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
    ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
    ${argEmpreendimentoBusca(company)}
    ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
    ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
    ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
    ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
    ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
    ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
    ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
    ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
    ) {
        count,
        imoveis {
            exclu
            id
            mini_banner_texto
            area_tipo
            pais
            qtd_salas
            tipo {
                id
                nome
            }
            categoria {
                id
                nome
            }
            cidade {
                id
                nome
            }
            bairro {
                id
                nome
                id_cidade
            }
            referencia
            data_entrega
            venda
            aluguel
            temporada
            data_edicao
            id
            d1
            disponibilidade
            endereco
            titulo
            descricao
            dormitorios
            suites
            banheiros
            garagems
            boxes
            origem
            bloquear_fotos
            mostrar_endereco
            preco_venda
            exibir_preco_venda
            moeda_imovel
            preco_iptu
            destaque
            link_video
            tour360
            preco_locacao
            esconder_home
            exibir_preco_locacao
            exibir_preco_iptu
            area_construida
            area_total
            terreno
            area_privativa
            area_util
            palavras_chave
            fotos {
                id
                id_imovel
                nome
                destaque
                url_spaces_cdn
                url_spaces_migracao
                posicao
                url_foto
                url_marca
            }
            caracteristicas {
                caracteristica {
                    id
                    nome
                    tipo
                }
            }
            visualizacoes
            preco_especial {
                preco_especial_venda
                preco_especial_locacao
            }
            empreendimento {
                id
                nome
                referencia
                endereco
                destaque
                meta_description
                meta_keywords
                meta_title
                descricao
                foto_destaque
            }
        }
        }
    }
    `;

    try {

        const data = signal
            ? await graphQLClient.request({ document: query, signal } as any)
            : await graphQLClient.request(query);

        return data;
    } catch (error: any) {
        // Resiliência (igual ao propertiesFilter): aproveita dados parciais quando o
        // backend erra só num campo (ex.: boxes Int com "33/34") em vez de zerar a lista.
        const partial = error?.response?.data;
        if (partial && Object.keys(partial).length > 0) return partial;
        return null;
    }
}

//query para pegar os imoveis para montar o mapa no site com latitude e longitude
export async function propertiesMapa(
    id_categoria: any,
    page: number,
    idCity?: number | null,
    idBairro?: any | null,
    idTipo?: any | null,
    referencia?: any,
    precoVendaMin?: number | null,
    precoVendaMax?: number | null,
    precoLocacaoMin?: number | null,
    precoLocacaoMax?: number | null,
    venda?: number,
    aluguel?: number,
    company?: number | number[],
    ordenar?: any,
    garagemsMin?: number | number[] | null,
    dormitoriosMin?: number | number[] | null,
    suitesMin?: number | number[] | null,
    banheirosMin?: number | number[] | null,
    agenciador?: Number | null,
    temporada?: number,
    mapa?: number,
    origin?: any, codsite?: number,
    signal?: AbortSignal,
    // MULTI-REGIÃO: cidades da região ativa (só aplicado sem idCity)
    idCidades?: number[] | null,
    // BUSCA POR IA (INC-0018 F4): os marcadores respeitam os mesmos mínimos/ordem da lista.
    extrasBuscaIa?: ExtrasBuscaIa | null
) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    // Atelier (utils/qtdMultiSelecao): 2+ números marcados chegam em LISTA → args *_lista, sem min/max.
    const argsListaQtd = argsQtdLista({ garagems: garagemsMin, dormitorios: dormitoriosMin, suites: suitesMin, banheiros: banheirosMin })
    garagemsMin = qtdEscalar(garagemsMin)
    dormitoriosMin = qtdEscalar(dormitoriosMin)
    suitesMin = qtdEscalar(suitesMin)
    banheirosMin = qtdEscalar(banheirosMin)
    let suitesMax = suitesMin
    if(suitesMin == 5)
        suitesMax = 0
    let garagemsMax = garagemsMin
    if(garagemsMin == 5)
        garagemsMax = 0
    let dormitoriosMax = dormitoriosMin
    if(dormitoriosMin == 5)
        dormitoriosMax = 0
    let banheirosMax = banheirosMin
    if(banheirosMin == 5)
        banheirosMax = 0
    const argsBuscaIa = argsGraphqlBuscaIa(extrasBuscaIa, {
        dormitorios_min: dormitoriosMin,
        suites_min: suitesMin,
        banheiros_min: banheirosMin,
        garagems_min: garagemsMin,
    })

    const endpoint = BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });
    const query = gql`
        {
            imoveis(pagina: ${page},
            ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
            ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
    ${argCategoriaBusca(id_categoria)}
    ${idCity ? `,id_cidade: ${idCity}` : ''}
    ${buildIdCidadesArg(idCity, idCidades)}
    ${idBairro ? `,id_bairro: ${idBairro}` : ''}
    ${idTipo ? `,id_tipo: ${idTipo}` : ''}
    ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
    ${aluguel ? `,aluguel: ${aluguel}` : ''}
    ${temporada ? `,temporada: ${temporada}` : ''}
    ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
    ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax}` : ''}
    ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
    ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
    ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
    ${argEmpreendimentoBusca(company)}
    ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
    ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
    ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
    ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
    ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
    ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
    ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
    ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
    ${mapa ? `,mapa: ${mapa}` : `,mapa: 12`}${argsBuscaIa}
    ) {
        count,
        imoveis {
            exclu
            id
            mini_banner_texto
            tipo {
                nome
            }
            referencia
            data_entrega
            venda
            aluguel
            qtd_salas
            temporada
            area_total
            area_tipo
            area_privativa
            bairro {
                nome
            }
            cidade {
                nome
            }
            temporada
            latitude_longitude
            titulo
            fotos {
                url_foto
                url_spaces_cdn
                url_spaces_migracao
            }
        }
        }
    }
    `;

    try {
const data = signal
            ? await graphQLClient.request({ document: query, signal } as any)
            : await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os imoveis em promoção para montar o mapa no site com latitude e longitude
export async function propertiesMapaPromocao(
    id_categoria: any,
    page: number,
    idCity?: number | null,
    idBairro?: any | null,
    idTipo?: any | null,
    referencia?: any,
    precoVendaMin?: number | null,
    precoVendaMax?: number | null,
    precoLocacaoMin?: number | null,
    precoLocacaoMax?: number | null,
    venda?: number,
    aluguel?: number,
    company?: number | number[],
    ordenar?: any,
    garagemsMin?: number | number[] | null,
    dormitoriosMin?: number | number[] | null,
    suitesMin?: number | number[] | null,
    banheirosMin?: number | number[] | null,
    agenciador?: Number | null,
    temporada?: number,
    mapa?: number,
    origin?: any, codsite?: number,
    signal?: AbortSignal,
    // MULTI-REGIÃO: cidades da região ativa (só aplicado sem idCity)
    idCidades?: number[] | null
) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    // Atelier (utils/qtdMultiSelecao): 2+ números marcados chegam em LISTA → args *_lista, sem min/max.
    const argsListaQtd = argsQtdLista({ garagems: garagemsMin, dormitorios: dormitoriosMin, suites: suitesMin, banheiros: banheirosMin })
    garagemsMin = qtdEscalar(garagemsMin)
    dormitoriosMin = qtdEscalar(dormitoriosMin)
    suitesMin = qtdEscalar(suitesMin)
    banheirosMin = qtdEscalar(banheirosMin)
    let suitesMax = suitesMin
    if(suitesMin == 5)
        suitesMax = 0
    let garagemsMax = garagemsMin
    if(garagemsMin == 5)
        garagemsMax = 0
    let dormitoriosMax = dormitoriosMin
    if(dormitoriosMin == 5)
        dormitoriosMax = 0
    let banheirosMax = banheirosMin
    if(banheirosMin == 5)
        banheirosMax = 0

    const endpoint = BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });
    const query = gql`
        {
            imoveis_promocao(pagina: ${page},
            ordenar: ${ordenar?.value?.split('-')[1] || 'Inclusao'},
            ordem: ${ordenar?.value?.split('-')[0] || 'desc'}
    ${argCategoriaBusca(id_categoria)}
    ${idCity ? `,id_cidade: ${idCity}` : ''}
    ${buildIdCidadesArg(idCity, idCidades)}
    ${idBairro ? `,id_bairro: ${idBairro}` : ''}
    ${idTipo ? `,id_tipo: ${idTipo}` : ''}
    ${referencia?.length > 2 ? `,referencia: ${referencia}` : (`${venda ? `,venda: ${venda}` : ''}
    ${aluguel ? `,aluguel: ${aluguel}` : ''}
    ${temporada ? `,temporada: ${temporada}` : ''}
    ${precoVendaMin ? `,preco_venda_min: ${precoVendaMin}` : ''}
    ${precoVendaMax ? `,preco_venda_max: ${precoVendaMax ? precoVendaMax : 100000000}` : ''}
    ${precoLocacaoMin ? `,preco_locacao_min: ${precoLocacaoMin}` : ''}
    ${agenciador ? `,id_agenciador: ${agenciador}` : ''}
    ${precoLocacaoMax ? `,preco_locacao_max: ${precoLocacaoMax}` : ''}`)}
    ${argEmpreendimentoBusca(company)}
    ${garagemsMin ? `,garagems_min: ${garagemsMin}` : ''}
    ${garagemsMax ? `,garagems_max: ${garagemsMax}` : ''}
    ${dormitoriosMin ? `,dormitorios_min: ${dormitoriosMin}` : ''}
    ${dormitoriosMax ? `,dormitorios_max: ${dormitoriosMax}` : ''}
    ${suitesMin ? `,suites_min: ${suitesMin}` : ''}
    ${suitesMax ? `,suites_max: ${suitesMax}` : ''}
    ${banheirosMin ? `,banheiros_min: ${banheirosMin}` : ''}
    ${banheirosMax ? `,banheiros_max: ${banheirosMax}` : ''}${argsListaQtd}
    ${mapa ? `,mapa: ${mapa}` : `,mapa: 12`}
    ) {
        count,
        imoveis {
            exclu
            id
            mini_banner_texto
            tipo {
                nome
            }
            referencia
            data_entrega
            venda
            qtd_salas
            origem
            aluguel
            temporada
            area_total
            area_tipo
            area_privativa
            bairro {
                nome
            }
            cidade {
                nome
            }
            temporada
            latitude_longitude
            titulo
            fotos {
                url_foto
                url_spaces_cdn
                url_spaces_migracao
            }
        }
        }
    }
    `;

    try {
        
const data = signal
            ? await graphQLClient.request({ document: query, signal } as any)
            : await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

export async function getManager(idImovel: number, origin?:  any, codsite?: number) {

    if (!origin && typeof window === 'undefined') {
        return null;
    }

    // Valida o ID como inteiro positivo suportado por GraphQL Int (32-bit).
    if (!isPositiveGraphqlInt(idImovel)) {
        return null;
    }


    const endpoint = BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `imovel_participantes${idImovel}`
        },
    });

    const query = gql`
        {
            imovel_participantes(id_imovel: ${idImovel}) {
                parceiro {
                    id
                    nome
                    telefone
                    telefone_2
                    email
                    mostrar_equipe
                    descricao
                    funcao {
                      id
                      nome
                    }
                    url_foto
                    facebook
                    instagram
                    linkedin
                    link_proprio
                    creci
                }
            }
        }
    `;

    try {
        
        const data = await graphQLClient.request(query);
        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os dados do corretor/gestor pelo id
export async function getManagerId(idManager: number, origin?: any, codsite?: number) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    const endpoint = BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `parceiros${idManager}`
        },
    });

    const query = gql`
        {
            parceiros (id: ${idManager}){
                id
                nome
                telefone
                telefone_2
                internacional
                email
                mostrar_equipe
                descricao
                funcao {
                    id
                    nome
                }
                url_foto
                facebook
                instagram
                linkedin
                link_proprio
                creci
            }
        }
    `;

    try {
        
const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os dados do lead pelo id
export async function getLeadId(idLead: number, origin?: any, codsite?: number) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    const endpoint = BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });

    const query = gql`
        {
            clienteLead(id_lead: ${idLead}) {
                nome
                telefone
                email
            }
        }
    `;

    try {
        
const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os imoveis semelhantes a um imóvel especifico
export async function propertiesRelated(
    id: number,
    idCity: number,
    idType: number,
    preco_venda?: number,
    preco_locacao?: number,
    origin?: any, codsite?: number
) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    const endpoint = BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });
    const query = gql`
        {
            imoveis_semelhantes(id: ${id}, ${idCity ? `id_cidade: ${idCity},` : ''}
            ${idType ? `id_tipo: ${idType}` : 'id_tipo:0'}
            ${preco_venda ? `,preco_venda: ${preco_venda}, venda: 1` : ''}
            ${preco_locacao ? `,preco_locacao: ${preco_locacao}, aluguel: 1` : ''}
    ) {
                id
                exclu
                pais
                tipo {
                    nome
                    id
                }
                categoria {
                    nome
                    id
                }
                cidade {
                    nome
                    id
                }
                bairro {
                    nome
                    id
                    id_cidade
                }
                referencia
                data_entrega
                venda
                aluguel
                endereco
                titulo
                descricao
                dormitorios
                suites
                banheiros
                garagems
                boxes
                mostrar_endereco
                exibir_preco_venda
                moeda_imovel
                preco_venda
                preco_iptu
                destaque
                link_video
                tour360
                preco_locacao
                exibir_preco_locacao
                exibir_preco_iptu
                area_construida
                latitude_longitude
                area_total
                area_tipo
                terreno
                area_privativa
                area_util
                palavras_chave
                fotos {
                    url_foto
                    url_spaces_cdn
                    url_spaces_migracao
                    destaque
                    id
                    id_imovel
                    nome
                    posicao
                    url_marca
                }
                empreendimento {
                    id
                    nome
                    foto_destaque
                }
            }
        }
    `;

    try {
        const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

// Id interno de um imóvel PUBLICADO a partir da REFERÊNCIA (o código que o cliente vê).
// Existe para as URLs do site antigo de clientes migrados, que identificavam a ficha pela
// referência e não pelo id (formato Mold Systems: /imovel/venda/casas/aracatuba/{bairro}/1269,
// e /imovel/{slug}/{REF} — Tecimob, cs2586/cs2606).
// Com o id, o fluxo normal de /imovel/[id] emite o redirect para a URL canônica.
export async function propertyIdByReferencia(
    referencia: string,
    origin?: string,
    codsite?: number,
    prodOrigin?: any
): Promise<number> {
    return (await propertyIdByReferenciaResultado(referencia, origin, codsite, prodOrigin)).id;
}

// Igual ao de cima, dizendo também se a API RESPONDEU: id 0 com `respondeu: true` = a referência não
// está publicada (dá para redirecionar de forma permanente); `respondeu: false` = erro/sem consulta
// (o redirect fica temporário, para um imóvel vivo não sair do Google por uma falha momentânea).
export async function propertyIdByReferenciaResultado(
    referencia: string,
    origin?: string,
    codsite?: number,
    prodOrigin?: any
): Promise<{ id: number; respondeu: boolean }> {
    // A referência é interpolada na query: só letras e dígitos passam.
    const ref = String(referencia || '').trim().replace(/[^A-Za-z0-9]/g, '');
    if (!ref) return { id: 0, respondeu: false };
    if ((!origin || origin === 'undefined') && typeof window === 'undefined') return { id: 0, respondeu: false };

    const endpoint = prodOrigin ? PROD_SSR_GQL_ENDPOINT : '/api/gql';
    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...(prodOrigin ? headers2 : headers),
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `imovel_por_referencia`,
        },
    });

    const query = gql`
        {
            imoveis_busca(pagina: 1, ordenar: Inclusao, ordem: desc, referencia: ["${ref}"]) {
                imoveis {
                    id
                    referencia
                }
            }
        }
    `;

    try {
        const data: any = await graphQLClient.request(query);
        const lista = data?.imoveis_busca?.imoveis || [];
        // O filtro casa Referencia OU ReferenciaAlternativa: a referência exata vence.
        const exato = lista.find(
            (i: any) => String(i?.referencia || '').trim().toUpperCase() === ref.toUpperCase()
        );
        return { id: Number((exato || lista[0])?.id) || 0, respondeu: true };
    } catch (error) {
        return { id: 0, respondeu: false };
    }
}

//query para pegar os dados específicos de um imóvel pelo id
export async function propertiesSpecific(
    id: number,
    origin?: string, codsite?: number, url?: any,prodOrigin?: any
) {
    if (((!origin || origin == undefined || origin == 'undefined') && typeof window === 'undefined') || !isPositiveGraphqlInt(id)) {
        return null;
    }
    let endpoint = BASE_URL;

    if(prodOrigin)
        endpoint = PROD_SSR_GQL_ENDPOINT
    else
        endpoint = '/api/gql'

    const graphQLClient = 
    prodOrigin ?
    new GraphQLClient(endpoint, {
        headers: {
            ...headers2,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `imovel_specific_${id}`
        },
    }) : new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `imovel_specific_${id}`
        },
    });

    const query = gql`
        {
            imovel(id: ${id}) {
                exclu
                id
                pais
                exibir_simulador
                bloquear_fotos
                usa_fotos_empreendimento
                tipo {
                id
                nome
                }
                categoria {
                id
                nome
                }
                cidade {
                id
                nome
                url_foto
                descricao
                }
                bairro {
                id
                nome
                id_cidade
                url_foto
                descricao
                }
                referencia
                data_entrega
                venda
                origem
                aluguel
                temporada
                data_edicao
                d1
                lado_direito
                lado_esquerdo
                frente
                fundos
                disponibilidade
                incorporacao
                latitude_longitude
                endereco
                titulo
                descricao
                dormitorios
                suites
                banheiros
                area_tipo
                garagems
                boxes
                mostrar_endereco
                complemento
                numero
                preco_venda
                exibir_preco_venda
                moeda_imovel
                preco_iptu
                destaque
                link_video
                preco_locacao
                exibir_preco_locacao
                exibir_preco_iptu
                exibir_preco_condominio
                preco_condominio
                area_construida
                area_total
                terreno
                tour360
                posicao_solar
                area_privativa
                area_util
                palavras_chave
                fotos {
                    id
                    id_imovel
                    nome
                    descricao
                    destaque
                    posicao
                    decoravel
                    id_foto_origem
                    url_spaces_cdn
                    url_spaces_migracao
                    url_foto
                    url_foto_menor
                    url_marca
                }
                caracteristicas {
                caracteristica {
                    id
                    nome
                    tipo
                }
                }
                visualizacoes
                preco_especial {
                preco_especial_venda
                preco_especial_locacao
                }
                empreendimento {
                    id
                    nome
                    referencia
                    endereco
                    destaque
                    foto_destaque
                    meta_description
                    meta_keywords
                    meta_title
                    descricao
                    fotos {
                        id
                        descricao
                        posicao
                        destaque
                        url_foto
                    }
                }
            }
        }
    `;

    try {
        
const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para pegar os dados específicos de um imóvel pelo id
export async function propertiesSpecific1(
    id: number,
    origin?: string, codsite?: number, url?: any,prodOrigin?: any
) {
    if (((!origin || origin == undefined || origin == 'undefined') && typeof window === 'undefined') || !isPositiveGraphqlInt(id)) {
        return null;
    }
    let endpoint = BASE_URL;

    if(prodOrigin)
        endpoint = PROD_SSR_GQL_ENDPOINT
    else
        endpoint = '/api/gql'

    const graphQLClient = 
    prodOrigin ?
    new GraphQLClient(endpoint, {
        headers: {
            ...headers2,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `imovel_specific1_${id}`
        },
    }) : new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: `imovel_specific1_${id}`
        },
    });

    const query = gql`
        {
            imovel(id: ${id}) {
                exclu
                id
                pais
                exibir_simulador
                usa_fotos_empreendimento
                bloquear_fotos
                tipo {
                id
                nome
                }
                categoria {
                id
                nome
                }
                cidade {
                id
                nome
                }
                bairro {
                id
                nome
                id_cidade
                }
                referencia
                data_entrega
                venda
                aluguel
                temporada
                data_edicao
                d1
                origem
                disponibilidade
                incorporacao
                latitude_longitude
                titulo
                lado_direito
                lado_esquerdo
                frente
                fundos
                descricao
                dormitorios
                suites
                banheiros
                area_tipo
                garagems
                boxes
                mostrar_endereco
                preco_venda
                exibir_preco_venda
                moeda_imovel
                preco_iptu
                destaque
                link_video
                preco_locacao
                exibir_preco_locacao
                exibir_preco_iptu
                exibir_preco_condominio
                preco_condominio
                area_construida
                area_total
                terreno
                tour360
                posicao_solar
                area_privativa
                area_util
                palavras_chave
                fotos {
                    id
                    id_imovel
                    nome
                    descricao
                    destaque
                    posicao
                    decoravel
                    id_foto_origem
                    url_spaces_cdn
                    url_spaces_migracao
                    url_foto
                    url_foto_menor
                    url_marca
                }
                caracteristicas {
                caracteristica {
                    id
                    nome
                    tipo
                }
                }
                visualizacoes
                preco_especial {
                preco_especial_venda
                preco_especial_locacao
                }
                empreendimento {
                    id
                    nome
                    referencia
                    endereco
                    destaque
                    meta_description
                    meta_keywords
                    meta_title
                    foto_destaque
                    descricao
                    fotos {
                        id
                        descricao
                        posicao
                        destaque
                        url_foto
                    }
                }
            }
        }
    `;

    try {
        
const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para enviar os dados do usuário que entrou em contato pelo formulário
export async function sendUser(
    internacional: boolean,
    utm_Campaign: string,
    utm_source: string,
    utm_Medium: string,
    utm_Id: string,
    mensagem: string,
    name: string,
    email: string,
    telephone: string,
    idProperties: number,
    agenciador?: number,
    origin?: string,
    codsite?: number,
    transacao_selecionada?: String,
    delegar_ao_agenciador?: number,
    leadOrigin?: string
    )
    {
        if (!origin && typeof window === 'undefined') {
            return null;
        }

        if (isLikelyBot()) return null;

        const endpoint = "/api/gql";

        const graphQLClient = new GraphQLClient(endpoint, {
            headers: {
                ...headers,
                dominion: typeof window !== 'undefined' ? window.location.origin : origin,
                'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
                codsite: codsite ? codsite : 0
            },
        });

    // MULTI-REGIÃO: resolvido AQUI (e não em cada chamador) direto dos sinais do
    // browser (path /r/<slug> > ?regiao= > cookie site_regiao). Com região ativa,
    // origem vira "site-<slug>" e, se utm_campaign veio vazio, cai em "regiao-<slug>".
    // sendUser roda só no client (endpoint /api/gql), então os sinais existem.
    const regiaoSlug = getRegiaoSlugFromBrowser();
    // 22/10 — chamador SEM nenhuma UTM (o WhatsApp flutuante manda as 4 vazias; a ficha só tem a
    // da URL atual): vale a UTM da 1ª visita guardada pelo utils/utmNoWhats (30 dias), para o lead
    // de campanha que navegou antes de converter não nascer como "site". UTM do chamador vence.
    const semUtm = (v: string) => !v || v === '""';
    if (typeof window !== 'undefined' && [utm_Campaign, utm_source, utm_Medium, utm_Id].every(semUtm)) {
        const primeiraVisita = guardaUtmDaPrimeiraVisita(window.location.href);
        if (primeiraVisita) {
            const comoGql = (v: string) => (v ? JSON.stringify(v) : '');
            utm_Campaign = comoGql(primeiraVisita.utm_campaign);
            utm_source = comoGql(primeiraVisita.utm_source);
            utm_Medium = comoGql(primeiraVisita.utm_medium);
            utm_Id = comoGql(primeiraVisita.utm_id);
        }
    }
    // chamadores passam utm via JSON.stringify → vazio pode chegar como '""'
    const utmCampaignVazio = !utm_Campaign || utm_Campaign === '""';
    // se o chamador informar a origem do lead (ex.: 'blog'), usa ela direto;
    // senão mantém o comportamento atual (site / site-<slug> multi-região)
    const origemLead = leadOrigin ? leadOrigin : (regiaoSlug ? `site-${regiaoSlug}` : 'site');

    const query = gql`
        mutation Atendimento {
            atendimento(nome: ${name}, email: ${email},mensagem: ${mensagem},internacional: ${internacional ? true : false},telefone: ${telephone}, id_imovel: ${idProperties}${
        agenciador && delegar_ao_agenciador ? ',id_filiado:' + agenciador : ''
    }, origem: "${origemLead}" ${!utmCampaignVazio ? ", utm_campaign: " + utm_Campaign : regiaoSlug ? `, utm_campaign: "regiao-${regiaoSlug}"` : ""}${utm_source ? ", utm_source: " + utm_source : ""}${utm_Medium ? ", utm_medium: " + utm_Medium : ""}${utm_Id ? ", utm_id: " + utm_Id : ""}${transacao_selecionada ? `, transacao_selecionada:  "${transacao_selecionada}"` : ""}) {
                id_atendimento
                id_cliente
                status
            }
        }
    `;

    let data: any = null;

    try {

        data = await graphQLClient.request(query);
    } catch (error) {
        return null;
    }

    // BLOCKLIST DE CADASTRO (sugestão #81, cs1939): quando o BFF recusa o visitante, o cadastro
    // NÃO pode se concluir. Sem interromper aqui, quem chamou gravaria `localStorage.user` logo
    // depois e as fotos bloqueadas seriam liberadas assim mesmo. O throw é proposital: garante
    // que nada rode depois do aviso, em todos os formulários que usam sendUser.
    if (isLeadBloqueadoResponse(data)) {
        // No servidor não há formulário para interromper: devolve como qualquer outra falha.
        if (typeof window === 'undefined') return null;
        handleLeadBloqueado();
        throw new LeadBloqueadoError();
    }

    return data;
}

export async function atualizaModelo(origin?: string, codsite?: number) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    if (isLikelyBot()) return null;

    const endpoint = "/api/gql";

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });

    const query = gql`
        mutation Mutation {
            modeloSite
        }
    `;

    try {
        
const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para registrar a visualização de um imóvel
export async function viewProperties(idProperties: number, idClient?: string, origin?: string, codsite?: number) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    if(!isPositiveGraphqlInt(idProperties))
        return null;

    if (isLikelyBot()) return null;

    const endpoint = "/api/gql";

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });

    const query = gql`
        mutation Visita {
            visita(id_imovel: ${idProperties} ${
        idClient ? `, id_cliente: ${idClient}` : ''
    }) {
                status
            }
        }
    `;

    try {
        const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}

//query para salvar os logs de acesso ao site
export async function salvaLogs(url: string, ip: string, origin?: string, codsite?: number) {
    if (!origin && typeof window === 'undefined') {
        return null;
    }

    if (isLikelyBot()) return null;

    const endpoint = "/api/gql";

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0
        },
    });

    const query = gql`
        mutation salvaLogAcesso {
            salvaLogAcesso(url: "${url}"${ip ? `, ip: "${ip}"` : ''})
        }
    `;

    try {
        
const data = await graphQLClient.request(query);

        return data;
    } catch (error) {
        return null;
    }
}
